import { axiosApiService } from '../axios';

/**
 * TL Dashboard API Service
 * Provides methods for fetching dashboard data specific to the TinkerersLab context.
 * All endpoints require authentication and use the TL header for context.
 */
const TLDashboardAPIs = {
    /**
     * Get user statistics data
     * @param {string} period - 'weekly' | 'monthly' | 'yearly'
     * @returns {Promise<Object>} User statistics including demographics, growth trends, footfall
     */
    getUserStatistics: async function (period = 'weekly') {
        try {
            const response = await axiosApiService.get(`/stats/tl-dashboard/user-statistics/`, {
                params: { period },
            });
            return response.data;
        } catch (error) {
            console.error('Error fetching user statistics:', error);
            throw error;
        }
    },

    /**
     * Get engagement data
     * @param {string} period - 'weekly' | 'monthly' | 'yearly'
     * @returns {Promise<Object>} Engagement data including peak hours, active users
     */
    getEngagement: async function (period = 'weekly') {
        try {
            const response = await axiosApiService.get(`/stats/tl-dashboard/engagement/`, {
                params: { period },
            });
            return response.data;
        } catch (error) {
            console.error('Error fetching engagement data:', error);
            throw error;
        }
    },

    /**
     * Get projects data
     * @param {string} period - 'weekly' | 'monthly' | 'yearly'
     * @returns {Promise<Object>} Projects data including types, distributions
     */
    getProjects: async function (period = 'weekly') {
        try {
            const response = await axiosApiService.get(`/stats/tl-dashboard/projects/`, {
                params: { period },
            });
            return response.data;
        } catch (error) {
            console.error('Error fetching projects data:', error);
            throw error;
        }
    },

    /**
     * Get machine usage data
     * @param {string} period - 'weekly' | 'monthly' | 'yearly'
     * @returns {Promise<Object>} Machine usage data including utilization, status
     */
    getMachineUsage: async function (period = 'weekly') {
        try {
            const response = await axiosApiService.get(`/stats/tl-dashboard/machine-usage/`, {
                params: { period },
            });
            return response.data;
        } catch (error) {
            console.error('Error fetching machine usage data:', error);
            throw error;
        }
    },

    /**
     * Get MIS data
     * @param {string} period - 'weekly' | 'monthly' | 'yearly'
     * @returns {Promise<Object>} MIS data including issue types, resolution status
     */
    getMISData: async function (period = 'weekly') {
        try {
            const response = await axiosApiService.get(`/stats/tl-dashboard/mis-data/`, {
                params: { period },
            });
            return response.data;
        } catch (error) {
            console.error('Error fetching MIS data:', error);
            throw error;
        }
    },

    /**
     * Export dashboard data as CSV
     * @param {string} dataType - 'user-statistics' | 'engagement' | 'projects' | 'machine-usage' | 'mis-data'
     * @param {string} period - 'weekly' | 'monthly' | 'yearly'
     * @returns {Promise<Blob>} CSV file blob for download
     */
    exportCSV: async function (dataType = 'user-statistics', period = 'weekly') {
        try {
            const response = await axiosApiService.get(`/stats/tl-dashboard/export-csv/`, {
                params: { data_type: dataType, period },
                responseType: 'blob',
            });
            return response.data;
        } catch (error) {
            console.error('Error exporting CSV:', error);
            throw error;
        }
    },
};

export default TLDashboardAPIs;
